import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from './AlertContext';
import Button from './Buttons';
import { useConfirmation } from './ConfirmationContext';
import Table from './Tables';
import { useAiKeyApi } from '../Services/AiKeyApiProvider';
import { AiKeyApi } from '../Services/AiKeyApiProviderTypes';
import useTablePagination from '../Hooks/useTablePagination';

function AiKeysTable() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getAiKeys, deleteAiKey } = useAiKeyApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [aiKeys, setAiKeys] = useState<AiKeyApi[]>([]);
  const [totalAiKeys, setTotalAiKeys] = useState<number>(0);

  const tableTitle = 'AI Keys';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  const aiKeyUrl = (aiKeyId: string) => `/organizations/${organizationId}/ai-keys/${aiKeyId}`;

  const columns = [
    { name: 'Type', selector: (row: AiKeyApi) => row.type, sortable: false },
    { name: 'Models', selector: (row: AiKeyApi) => row.models.join(', '), sortable: false },
    {
      name: 'Status',
      selector: (row: AiKeyApi) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
  ];

  const handleRowClicked = (row: AiKeyApi): void => {
    navigate(aiKeyUrl(row.id), { state: { organizationId } });
  };

  const handleDelete = async (selectedId: string[]): Promise<void> => {
    if (!selectedId[0]) return;

    showConfirmation(`Are you sure you want to delete the selected item?`, async () => {
      try {
        setLoading(true);
        await deleteAiKey(selectedId[0]);
        await fetchAiKeys(storedSkip(), storedLimit());
        showAlert('success', 'Item was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      } finally {
        setLoading(false);
      }
    });
  };

  const fetchAiKeys = async (skip: number, limit: number): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await getAiKeys(skip, limit);
      setAiKeys(response.items);
      setTotalAiKeys(response.totalCount);
    } catch (error) {
      setError((error as Error).message);
      showAlert('fail', 'Failed to fetch AI keys!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAiKeys(storedSkip(), storedLimit());
  }, []);

  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <Table
        title={tableTitle}
        columns={columns}
        data={aiKeys}
        selectableRows
        selectableRowsHighlight
        paginationPerPage={initialPageSize}
        progressPending={loading}
        paginationTotalRows={totalAiKeys}
        onRowClicked={handleRowClicked}
        onDelete={handleDelete}
        onChangePage={fetchAiKeys}
        getRowUrl={(row: AiKeyApi) => aiKeyUrl(row.id)}
      />
      <Button background="color2" size="large" onClick={() => navigate(aiKeyUrl('new-ai-key'))}>
        Add AI Key
      </Button>
    </>
  );
}

export default AiKeysTable;
